/**
 * Device Management Page (Super Admin)
 * 기기 관리 페이지 - 태블릿 기기 등록/조회/관리
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config/api';

// 기기 타입 정의
interface Device {
  device_id: string;
  organization_name: string;
  registered_at: string;
  expires_at: string | null;
  is_active: boolean;
  memo: string | null;
}

// 등록 폼 타입
interface DeviceForm {
  device_id: string;
  organization_name: string;
  license_type: 'permanent' | 'term';
  expires_at: string;
  memo: string;
}

const initialForm: DeviceForm = {
  device_id: '',
  organization_name: '',
  license_type: 'permanent',
  expires_at: '',
  memo: '',
};

const DeviceManagementPage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null); // null=신규, Device=편집
  const [form, setForm] = useState<DeviceForm>(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const getToken = () => {
    return localStorage.getItem('admin_token') || localStorage.getItem('token');
  };

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/devices`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      setDevices(Array.isArray(data) ? data : data.devices || []);
    } catch (err: any) {
      setError(err.message || '기기 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleOpenEdit = (device: Device) => {
    setEditingDevice(device);
    setForm({
      device_id: device.device_id,
      organization_name: device.organization_name || '',
      license_type: device.expires_at ? 'term' : 'permanent',
      expires_at: device.expires_at ? device.expires_at.split('T')[0] : '',
      memo: device.memo || '',
    });
    setShowModal(true);
  };

  const handleOpenNew = () => {
    setEditingDevice(null);
    setForm(initialForm);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.device_id.trim() || !form.organization_name.trim()) {
      alert('기기 ID와 업체명은 필수 입력입니다.');
      return;
    }

    setSubmitting(true);
    try {
      const token = getToken();
      const body: any = {
        device_id: form.device_id.trim(),
        organization_name: form.organization_name.trim(),
        memo: form.memo.trim() || null,
      };
      if (form.license_type === 'term' && form.expires_at) {
        body.expires_at = form.expires_at;
      } else {
        body.expires_at = null;
      }

      let response;
      if (editingDevice) {
        // 편집 모드: PUT
        response = await fetch(
          `${API_BASE_URL}/api/v1/admin/devices/${encodeURIComponent(editingDevice.device_id)}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
          }
        );
      } else {
        // 신규 등록: POST
        response = await fetch(`${API_BASE_URL}/api/v1/admin/devices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `${editingDevice ? '수정' : '등록'} 실패 (HTTP ${response.status})`);
      }

      setShowModal(false);
      setForm(initialForm);
      setEditingDevice(null);
      fetchDevices();
    } catch (err: any) {
      alert(err.message || '기기 저장에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (deviceId: string) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/devices/${encodeURIComponent(deviceId)}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `삭제 실패 (HTTP ${response.status})`);
      }

      setDeleteConfirm(null);
      fetchDevices();
    } catch (err: any) {
      alert(err.message || '기기 삭제에 실패했습니다.');
    }
  };

  const handleToggleActive = async (device: Device) => {
    try {
      const token = getToken();
      const response = await fetch(
        `${API_BASE_URL}/api/v1/admin/devices/${encodeURIComponent(device.device_id)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ is_active: !device.is_active }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `상태 변경 실패 (HTTP ${response.status})`);
      }

      fetchDevices();
    } catch (err: any) {
      alert(err.message || '상태 변경에 실패했습니다.');
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '영구';
    try {
      return new Date(dateStr).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  // 통계
  const activeCount = devices.filter((d) => d.is_active).length;
  const inactiveCount = devices.filter((d) => !d.is_active).length;
  const permanentCount = devices.filter((d) => !d.expires_at).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 페이지 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center mb-2">
                <span className="text-3xl mr-3" role="img" aria-label="device">
                  📱
                </span>
                <h1 className="text-3xl font-bold text-gray-900">기기 관리</h1>
              </div>
              <p className="text-gray-600">
                태블릿 기기를 등록하고 라이선스를 관리합니다.
              </p>
            </div>
            <button
              onClick={handleOpenNew}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              새 기기 등록
            </button>
          </div>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">전체 기기</p>
            <p className="text-2xl font-bold text-gray-900">{devices.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">활성</p>
            <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">비활성</p>
            <p className="text-2xl font-bold text-red-600">{inactiveCount}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">영구 라이선스</p>
            <p className="text-2xl font-bold text-indigo-600">{permanentCount}</p>
          </div>
        </div>

        {/* 에러 표시 */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p className="font-medium">오류</p>
            <p className="text-sm">{error}</p>
            <button
              onClick={fetchDevices}
              className="mt-2 text-sm text-red-600 underline hover:text-red-800"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* 기기 테이블 */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4"></div>
              <p>기기 목록을 불러오는 중...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <span className="text-4xl mb-4 block" role="img" aria-label="empty">
                📱
              </span>
              <p className="text-lg font-medium">등록된 기기가 없습니다</p>
              <p className="text-sm mt-1">위의 "새 기기 등록" 버튼을 눌러 기기를 추가하세요.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      기기 ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      업체명
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      등록일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      만료일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      메모
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      액션
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {devices.map((device) => (
                    <tr key={device.device_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono font-medium text-gray-900">
                          {device.device_id}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {device.organization_name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-500">
                          {formatDateTime(device.registered_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`text-sm ${
                            device.expires_at ? 'text-gray-500' : 'text-indigo-600 font-medium'
                          }`}
                        >
                          {formatDate(device.expires_at)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {device.is_active ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            활성
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            비활성
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-500 max-w-xs truncate block">
                          {device.memo || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        <button
                          onClick={() => handleOpenEdit(device)}
                          className="mr-2 px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                        >
                          편집
                        </button>
                        <button
                          onClick={() => handleToggleActive(device)}
                          className={`mr-2 px-3 py-1 rounded text-xs font-medium transition-colors ${
                            device.is_active
                              ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          {device.is_active ? '비활성화' : '활성화'}
                        </button>
                        {deleteConfirm === device.device_id ? (
                          <span className="inline-flex items-center space-x-1">
                            <button
                              onClick={() => handleDelete(device.device_id)}
                              className="px-3 py-1 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700"
                            >
                              확인
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="px-3 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300"
                            >
                              취소
                            </button>
                          </span>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(device.device_id)}
                            className="px-3 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200 transition-colors"
                          >
                            삭제
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 새 기기 등록 모달 */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* 배경 오버레이 */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
              onClick={() => setShowModal(false)}
            ></div>

            {/* 모달 */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 z-10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">{editingDevice ? '기기 편집' : '새 기기 등록'}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 기기 ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    기기 ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.device_id}
                    onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                    placeholder="예: TABLET-001"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                {/* 업체명 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    업체명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.organization_name}
                    onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                    placeholder="예: 이루는자리 독서실"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>

                {/* 라이선스 타입 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    라이선스
                  </label>
                  <div className="flex space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="license_type"
                        value="permanent"
                        checked={form.license_type === 'permanent'}
                        onChange={() => setForm({ ...form, license_type: 'permanent', expires_at: '' })}
                        className="form-radio text-indigo-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">영구</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="license_type"
                        value="term"
                        checked={form.license_type === 'term'}
                        onChange={() => setForm({ ...form, license_type: 'term' })}
                        className="form-radio text-indigo-600"
                      />
                      <span className="ml-2 text-sm text-gray-700">기간제</span>
                    </label>
                  </div>
                </div>

                {/* 만료일 (기간제일 때만) */}
                {form.license_type === 'term' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      만료일 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={form.expires_at}
                      onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>
                )}

                {/* 메모 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                  <textarea
                    value={form.memo}
                    onChange={(e) => setForm({ ...form, memo: e.target.value })}
                    placeholder="기기에 대한 메모 (선택)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                </div>

                {/* 버튼 */}
                <div className="flex justify-end space-x-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {submitting ? '등록 중...' : '등록'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceManagementPage;
